import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { searchCompanies } from "../lib/api";

export default function SearchBar({ large = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const data = await searchCompanies(q);
        setResults(data);
        setOpen(true);
      } catch (e) {
        console.warn("Company search failed", e);
        setResults([]);
        setOpen(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const go = (ticker) => {
    setOpen(false);
    setQ("");
    navigate(`/company/${ticker}`);
  };

  const submit = (e) => {
    e.preventDefault();
    if (results[0]) go(results[0].ticker);
  };

  return (
    <div ref={boxRef} className="relative w-full">
      <form onSubmit={submit} className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => results.length && setOpen(true)}
            placeholder="Search by ticker or company name — try QCOM, Nvidia, SWKS…"
            className={`w-full rounded-lg border border-border bg-white px-4 ${
              large ? "py-4 text-base" : "py-2.5 text-sm"
            } outline-none transition-colors focus:border-neutral-900`}
          />
        </div>
        <button
          data-testid="search-submit-btn"
          type="submit"
          className={`flex items-center gap-2 rounded-lg bg-neutral-900 px-5 ${
            large ? "py-4" : "py-2.5"
          } font-medium text-white transition-transform active:scale-95 hover:bg-neutral-800`}
        >
          <Search className="h-4 w-4" strokeWidth={2} />
          <span className="hidden sm:inline">Search</span>
        </button>
      </form>

      {open && results.length > 0 && (
        <div
          data-testid="search-results"
          className="absolute left-0 right-0 top-full mt-2 z-30 overflow-hidden rounded-lg border border-border bg-white shadow-lg"
        >
          {results.map((r) => (
            <button
              key={r.ticker}
              data-testid={`search-result-${r.ticker}`}
              onClick={() => go(r.ticker)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-neutral-50"
            >
              <div>
                <span className="font-semibold text-neutral-900">{r.name}</span>
                <span className="ml-2 text-xs font-mono text-neutral-400">{r.ticker}</span>
              </div>
              <span className="text-xs text-neutral-400">{r.sector}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
